import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/shared/hooks/useAuth";
import { supabase } from "../lib/supabase";
import { useUserData } from "../hooks/useUserData";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";
import { SUPABASE_ERROR_CODES } from "@/shared/constants/constants";
import { parsePersonDateAsLocal } from "@/shared/utils/formatters";
import { parseAddress } from "@/shared/utils/addressUtils";
import { useCanonicalUserAddress } from "@/shared/hooks/useCanonicalAddress";

import type { AddressParts } from "@/shared/utils/addressUtils";

interface SupabaseUserData {
  user_id: number; // This is what Supabase returns, not 'id'
  auth_id: string;
  email: string | null;
  full_name: string | null;
  first_name?: string | null; // Not selected in query
  last_name?: string | null; // Not selected in query
  phone_number: string | null;
  address: string | null;
  contact_by_phone: boolean | null;
  contact_by_email: boolean | null;
  completed_onboarding: boolean | null;
  created_at: string | null;
  date_of_birth?: string | null;
  requested_deletion?: boolean | null;
  completed_authorization?: boolean | null;
  revoked_authorization?: boolean | null;
  tpa_signature_audit_data?: Record<string, unknown> | null;
  prepopulation_data?: Record<string, string> | null;
  updated_at?: string | null;
}

export interface UserSettingsData {
  // Supabase data (prioritized)
  supabaseData: SupabaseUserData | null;

  // Computed values with fallbacks
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  address: string;
  addressParts: AddressParts;
  addressIsCanonical: boolean;
  dateOfBirth: Date | undefined;
  fullName: string;
  contactByPhone: boolean;
  contactByEmail: boolean;
  requestedDeletion: boolean;
  completedAuthorization: boolean;
  tpaSignatures: Record<string, unknown> | null;
  prepopulationData: Record<string, string>;

  // Loading and refetch
  isLoading: boolean;
  error: Error | null;
  hasSupabaseData: boolean;
  refetch: () => void;
}

export function useUserSettingsData(): UserSettingsData {
  const { user: authUser } = useAuth();
  const {
    firstName: backendFirstName,
    lastName: backendLastName,
    email: backendEmail,
  } = useUserData();

  const {
    data: supabaseData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["userSettingsSupabase", authUser?.sub],
    queryFn: async (): Promise<SupabaseUserData | null> => {
      if (!authUser?.sub) {
        return null;
      }

      try {
        const { data, error } = await supabase
          .from("users")
          .select(
            "user_id, auth_id, email, full_name, phone_number, address, contact_by_phone, contact_by_email, created_at, completed_onboarding, completed_authorization, revoked_authorization, tpa_signature_audit_data, prepopulation_data, date_of_birth, requested_deletion",
          )
          .eq("auth_id", authUser.sub)
          .single();

        if (error) {
          if (error.code === SUPABASE_ERROR_CODES.NO_ROWS_FOUND) {
            // Not found - this is expected for new users
            return null;
          }
          enqueueSnackbar(TOAST.GENERIC_ERROR, {
            variant: "error",
          });
          return null;
        }

        return data;
      } catch {
        enqueueSnackbar(TOAST.GENERIC_ERROR, {
          variant: "error",
        });
        return null;
      }
    },
    enabled: !!authUser?.sub,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  const { data: canonical } = useCanonicalUserAddress(supabaseData?.user_id);

  // Compute values with fallbacks - prioritize Supabase data
  const firstName = supabaseData?.full_name?.split(" ")[0] || backendFirstName || "";
  const lastName = supabaseData?.full_name?.split(" ").slice(1).join(" ") || backendLastName || "";
  const email = supabaseData?.email || backendEmail || "";
  const phoneNumber = supabaseData?.phone_number || "";
  const address = supabaseData?.address || "";
  const addressParts: AddressParts = canonical
    ? {
        streetAddress: canonical.street1,
        apartment: canonical.street2 ?? "",
        city: canonical.city ?? "",
        state: canonical.state ?? "",
        zipCode: canonical.zip,
      }
    : parseAddress(address);
  const dateOfBirth = parsePersonDateAsLocal(supabaseData?.date_of_birth);
  const contactByPhone = supabaseData?.contact_by_phone ?? true; // Default to true
  const contactByEmail = supabaseData?.contact_by_email ?? true; // Default to true
  const requestedDeletion = supabaseData?.requested_deletion ?? false; // Default to false
  const completedAuthorization = supabaseData?.completed_authorization ?? false;
  const tpaSignatures = (supabaseData?.tpa_signature_audit_data ?? null) as Record<
    string,
    unknown
  > | null;

  // Use Supabase full_name if available, otherwise construct from backend data
  const fullName = supabaseData?.full_name || `${backendFirstName} ${backendLastName}`.trim() || "";

  // Combine Supabase data with backend data, overriding with computed values
  const hasSupabaseData = !!supabaseData;

  return {
    supabaseData: supabaseData || null,
    firstName,
    lastName,
    email,
    phoneNumber,
    address,
    addressParts,
    addressIsCanonical: !!canonical,
    dateOfBirth,
    fullName,
    contactByPhone,
    contactByEmail,
    requestedDeletion,
    completedAuthorization,
    tpaSignatures,
    prepopulationData: supabaseData?.prepopulation_data || {},
    isLoading,
    error,
    hasSupabaseData,
    refetch,
  };
}
